import { useTranslation } from "react-i18next";
import useAboutContent from "src/hooks/use-about-content";
import Footer from "src/sections/components/footer/footer";
import PageHero from "src/sections/components/page-hero";
import PatternLayer from "src/sections/components/pattern-layer";
import PhotoBand from "src/sections/components/photo-band";
import RequestCta from "src/sections/components/request-cta";
import ReviewsCarousel from "src/sections/reviews/reviews-carousel";
import SakuraDivider from "src/sections/components/sakura-divider";
import TouristsCarousel from "src/sections/components/tourists-carousel";
import { usePageMeta } from "src/utils/seo";

// СТРАНИЦА «О НАС».
//
// На главной блок «о нас» короткий — там человек ещё выбирает, стоит ли
// вообще читать. Сюда он приходит, когда уже почти решился и проверяет,
// кому отдаёт 2600 $: кто эти люди, почему им можно верить, что стоит за
// цифрами. Поэтому здесь развёрнутый текст, история и все преимущества.
//
// ОТКУДА КОНТЕНТ. Рассказ, преимущества и цифры — из админки (настройка
// `about`), а чего владелец не заполнил, берётся из переводов: этим занят
// общий хук useAboutContent, тот же, что у блока на главной. Рассказ про
// синергию двух стран остаётся только здесь — это постоянный текст бренда,
// он не меняется от заезда к заезду.

export default function AboutPage() {
  const { t } = useTranslation();
  const { text, advantages, stats } = useAboutContent();

  usePageMeta({
    title: t("aboutPage.title"),
    description: t("aboutPage.subtitle"),
  });

  // Абзацы разделяются пустой строкой — так текст приходит из админки.
  const paragraphs = text.value.split(/\n{2,}/).map((x) => x.trim()).filter(Boolean);

  return (
    <div className="min-h-screen w-full flex flex-col bg-kj-dark text-kj-ink">
      <PageHero
        eyebrow={t("aboutPage.eyebrow")}
        title={t("aboutPage.title")}
        subtitle={t("aboutPage.subtitle")}
      />

      <div className="relative">
        {/* Полоса стоит на уровне рассказа о синергии двух стран — там, где
            текста больше всего и страница иначе выглядит сплошной заливкой. */}
        <PhotoBand mirrored className="top-[520px] h-[520px] sm:h-[680px]" />

      <main className="relative z-10 max-w-[1526px] w-full mx-auto px-4 mt-10 sm:mt-14 mb-12 flex flex-col gap-y-12 sm:gap-y-16">
        {/* КТО МЫ. Две колонки: слева рассказ, справа цифры — читаются
            вместе, поэтому и стоят рядом, а не одно под другим. */}
        <section className="grid gap-10 lg:gap-16 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
          <div>
            <h2 className="text-xl sm:text-2xl font-extrabold uppercase tracking-tight text-kj-ink">
              {t("aboutPage.leadTitle")}
            </h2>
            {/* Текст из админки главнее: владелец пишет о себе сам. Пока он
                пуст, страница рассказывает то же самое словами бренда — оба
                текста сразу показывать нельзя, они об одном и том же. */}
            {paragraphs.map((paragraph, i) => (
              <p
                key={i}
                {...text.attrs}
                className="mt-4 text-base sm:text-lg text-kj-sub leading-relaxed max-w-[64ch]"
              >
                {paragraph}
              </p>
            ))}
          </div>

          <div className="relative overflow-hidden rounded-2xl border border-kj-line bg-kj-card/50 p-6 sm:p-8 self-start">
            <PatternLayer kind="asanoha" />
            <h2 className="relative text-xs font-extrabold uppercase tracking-[0.2em] text-kj-gold">
              {t("aboutPage.statsTitle")}
            </h2>
            <dl className="relative mt-6 grid grid-cols-2 gap-x-6 gap-y-7">
              {stats.map((stat, i) => (
                <div key={i}>
                  <dt className="sr-only">{stat.label.value}</dt>
                  <dd>
                    <span className="block text-3xl sm:text-4xl font-extrabold text-kj-gold tabular-nums leading-none">
                      {stat.value}
                    </span>
                    <span
                      {...stat.label.attrs}
                      className="mt-2 block text-xs sm:text-sm uppercase tracking-[0.1em] text-kj-sub leading-snug"
                    >
                      {stat.label.value}
                    </span>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <SakuraDivider variant="left" season="sakura" className="!px-0" />

        {/* СИНЕРГИЯ ДВУХ СТРАН — то, чего нет ни у одного конкурента:
            объяснение, почему именно казахстанская команда в Японии. */}
        <section className="relative overflow-hidden rounded-2xl border border-kj-line bg-kj-card/40 p-6 sm:p-10 lg:p-14">
          <PatternLayer kind="seigaiha" />
          <h2 className="relative text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-kj-ink text-balance max-w-[22ch]">
            {t("aboutPage.synergyTitle")}
          </h2>
          <div className="relative mt-6 grid gap-6 lg:grid-cols-2 lg:gap-12">
            <p className="text-base sm:text-lg text-kj-sub leading-relaxed">
              {t("aboutPage.synergy1")}
            </p>
            <p className="text-base sm:text-lg text-kj-sub leading-relaxed">
              {t("aboutPage.synergy2")}
            </p>
          </div>
        </section>

        {/* ПРЕИМУЩЕСТВА */}
        <section>
          <h2 className="text-xl sm:text-2xl font-extrabold uppercase tracking-tight text-kj-ink">
            {t("aboutPage.advantagesTitle")}
          </h2>
          <ul className="mt-7 grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
            {advantages.map((item, i) => (
              <li key={i} className="border-t border-kj-line pt-5">
                <span className="block text-xs font-extrabold tabular-nums tracking-[0.2em] text-kj-gold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3
                  {...item.title.attrs}
                  className="mt-2 text-base sm:text-lg font-extrabold uppercase tracking-tight text-kj-ink leading-snug"
                >
                  {item.title.value}
                </h3>
                <p
                  {...item.text.attrs}
                  className="mt-2 text-sm sm:text-base text-kj-sub leading-relaxed"
                >
                  {item.text.value}
                </p>
              </li>
            ))}
          </ul>
        </section>

        {/* Наши туристы в Японии — живые кадры групп сразу перед заявкой:
            после рассказа «кто мы» человек видит, с кем и куда он поедет. */}
        <TouristsCarousel />

        <RequestCta />

        {/* Последнее на странице — не наши слова о себе, а чужие фотографии:
            скриншоты переписок и кадры из поездок. Рассказ «кто мы» ничего не
            стоит без них, поэтому лента стоит в самом низу, после заявки. */}
        <ReviewsCarousel />
      </main>
      </div>

      <Footer />
    </div>
  );
}
